/**
 * @file App.jsx
 * @description Root component aplikasi.
 * Mengatur routing, global styles, dan inisialisasi AOS animation.
 */
import React, { useEffect, Suspense, useState } from 'react';
import { Switch, Route } from 'react-router-dom';
import AOS from 'aos';
import { useGetOrganizationContentQuery } from '../services/apiSlice';

// Global styles
import '../assets/css/pe-icon-7.css';
import 'aos/dist/aos.css';
import '../assets/scss/themes.scss';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Route definitions
import routes from './routes';

// Loading fallback component
import LoadingOverlay from '../components/common/LoadingOverlay';
import UnderConstruction from '../components/common/UnderConstruction/UnderConstruction';

/**
 * App — Root functional component.
 * Menggantikan class component sebelumnya dengan hooks.
 */
const App = () => {
  const { data, isLoading, isError } = useGetOrganizationContentQuery();
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    AOS.init();
  }, []);

  useEffect(() => {
    // Jika masih loading API atau error, jangan cek gambar
    if (isLoading || isError || !data?.organizationId) return;

    // Set fallback timer (maksimal 3 detik loading, jangan buat user menunggu selamanya)
    const fallbackTimer = setTimeout(() => {
      setImagesLoaded(true);
    }, 3000);

    // Tunggu sejenak agar React merender elemen img ke DOM
    const renderTimer = setTimeout(() => {
      // Filter image, abaikan image dari Google Maps atau domain eksternal lain 
      // yang bisa terus me-load tanpa henti
      const images = Array.from(document.images).filter(img => {
        const src = img.src || '';
        return !src.includes('maps.googleapis.com') && 
               !src.includes('gstatic.com') &&
               !src.includes('google.com');
      });
      
      if (images.length === 0) {
        setImagesLoaded(true);
        clearTimeout(fallbackTimer);
        return;
      }

      let loadedCount = 0;
      const incrementLoaded = () => {
        loadedCount++;
        if (loadedCount === images.length) {
          setImagesLoaded(true);
          clearTimeout(fallbackTimer);
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          incrementLoaded();
        } else {
          img.addEventListener('load', incrementLoaded, { once: true });
          img.addEventListener('error', incrementLoaded, { once: true });
        }
      });
    }, 100);

    return () => {
      clearTimeout(renderTimer);
      clearTimeout(fallbackTimer);
    };
  }, [isLoading, isError, data]);

  // Jika API mengembalikan error, atau data gagal dimuat (kosong) / tidak ada organizationId
  if (!isLoading && (isError || !data?.organizationId)) {
    return <UnderConstruction />;
  }

  const isAppReady = !isLoading && imagesLoaded;

  return (
    <>
      {!isAppReady && <LoadingOverlay />}
      <Suspense fallback={<LoadingOverlay />}>
        <Switch>
          {routes.map((route, index) => (
            <Route
              key={index}
              path={route.path}
              component={route.component}
            />
          ))}
        </Switch>
      </Suspense>
    </>
  );
};

export default App;
